
/**
 * AI Chat Service - Keyword Matcher
 * Handles keyword matching for the AI chat service
 */

import { KnowledgeBase } from './knowledge';

/**
 * Matches user message with keywords in the knowledge base
 * @param message User message
 * @param knowledge Knowledge base with responses
 * @returns Best matching response or undefined if no match found
 */
export const findKeywordMatch = (message: string, knowledge: KnowledgeBase): string | undefined => {
  const lowerMessage = message.toLowerCase();
  
  // Special sign-in related keyword handling
  if (lowerMessage.includes('login') || lowerMessage.includes('sign in')) {
    // Handle sign-in problems with more specific troubleshooting
    if (lowerMessage.includes('problem') || lowerMessage.includes('issue') || lowerMessage.includes('error')) {
      if (lowerMessage.includes('password')) {
        return "If you're having password issues: 1) Check your caps lock, 2) Ensure you're using the correct password, 3) Click 'Forgot Password' to reset if needed, 4) If you recently changed your password, try the new one, 5) Make sure there are no extra spaces before or after your password.";
      }
      if (lowerMessage.includes('email')) {
        return "For email-related login issues: 1) Double-check your email address for typos, 2) Ensure you're using the email you registered with, 3) Check if you received a verification email when you signed up, 4) Contact support if you're using the correct email but still can't access your account.";
      }
      return "If you're having trouble signing in: 1) Verify your email is correct, 2) Check your password, 3) Ensure your internet connection is stable, 4) Clear your browser cache, 5) Try using incognito mode, 6) If problems persist, click 'Forgot Password' or contact support.";
    }
    
    // Handle first-time user guidance
    if (lowerMessage.includes('first time') || lowerMessage.includes('new user')) {
      return "Welcome to Bluetooth$! For first-time users: 1) Click 'Create Account' to start, 2) Enter your email and create a secure password, 3) Verify your email address, 4) Complete your profile setup. We prioritize security in every step.";
    }
    
    // Handle password reset process
    if (lowerMessage.includes('forgot') || lowerMessage.includes('reset')) {
      return "To reset your password: 1) Click 'Forgot Password' on the sign-in page, 2) Enter your registered email, 3) Check your inbox for a reset link (check spam folder too), 4) Click the link and create a new password, 5) Your new password must be at least 8 characters long with mixed case letters and numbers.";
    }
    
    // Handle verification issues
    if (lowerMessage.includes('verify') || lowerMessage.includes('verification')) {
      return "For verification issues: 1) Check your email including spam folder for the verification link, 2) Click the link to verify your account, 3) If the link has expired, you can request a new one from the sign-in page, 4) Make sure you're using the same browser where you started the process.";
    }
  }

  // Transactions specific responses
  if (lowerMessage.includes('transaction')) {
    if (lowerMessage.includes('history') || lowerMessage.includes('record')) {
      return "Your transaction history is available in your account dashboard. It shows all payments sent and received, including dates, amounts, recipients, and payment methods used.";
    }
    if (lowerMessage.includes('fail') || lowerMessage.includes('error')) {
      return "If a transaction failed: 1) Check your card details, 2) Ensure sufficient funds, 3) Verify the recipient ID is correct, 4) Try using one of our test cards like 4242 4242 4242 4242 to verify the system is working correctly.";
    }
    if (lowerMessage.includes('pending')) {
      return "Pending transactions typically complete within 24 hours. If a transaction remains pending for longer, it may be due to verification processes or banking delays.";
    }
    if (lowerMessage.includes('cancel') || lowerMessage.includes('refund')) {
      return "To request a refund for a transaction, go to your transaction history, locate the transaction, and select 'Request Refund'. Note that not all transactions are eligible for refunds.";
    }
  }

  // Card verification responses
  if ((lowerMessage.includes('card') || lowerMessage.includes('credit')) && 
      (lowerMessage.includes('verif') || lowerMessage.includes('check'))) {
    return "Card verification uses our AI system to analyze the card details and assign a risk score. For testing, you can use Stripe test cards like 4242 4242 4242 4242, 4000 0566 5566 5556, or 5555 5555 5555 4444.";
  }

  // Test card responses
  if (lowerMessage.includes('test') && lowerMessage.includes('card')) {
    return "You can use these Stripe test cards: 4242 4242 4242 4242 (Visa), 4000 0566 5566 5556 (Visa debit), or 5555 5555 5555 4444 (Mastercard). Use any future expiry date, any 3-digit CVC, and any postal code.";
  }
  
  // General app question handling
  if (lowerMessage.includes('what') && lowerMessage.includes('app')) {
    return "Bluetooth$ is a secure financial application that allows you to transfer money via Bluetooth, email, or to any verified user account with end-to-end encryption.";
  }
  
  if (lowerMessage.includes('how') && lowerMessage.includes('work')) {
    return "Bluetooth$ works by establishing secure connections between verified devices for direct money transfers. For non-verified recipients, we use secure email transfers instead.";
  }
  
  // Specific Stripe knowledge
  if (lowerMessage.includes('stripe')) {
    if (lowerMessage.includes('integration') || lowerMessage.includes('connect')) {
      return "Bluetooth$ integrates with Stripe's secure payment processing platform for handling all card payments and verification processes.";
    }
    if (lowerMessage.includes('payment intent') || lowerMessage.includes('intent')) {
      return "Stripe Payment Intents represent your intention to collect payment from a customer, capturing the entire payment lifecycle within a single object.";
    }
    if (lowerMessage.includes('token') || lowerMessage.includes('tokeniz')) {
      return "Card tokenization with Stripe replaces sensitive card data with a secure token, enhancing security by ensuring actual card details are never stored on our servers.";
    }
  }

  // Process multi-word keywords first (more specific matches)
  const multiWordKeywords = Object.keys(knowledge.responses).filter(key => key.includes(' '));
  for (const key of multiWordKeywords) {
    if (lowerMessage.includes(key)) {
      const responseList = knowledge.responses[key];
      return responseList[Math.floor(Math.random() * responseList.length)];
    }
  }
  
  // Then process single-word keywords
  const singleWordKeywords = Object.keys(knowledge.responses).filter(key => !key.includes(' '));
  for (const key of singleWordKeywords) {
    // Only match on word boundaries to prevent partial word matching
    // e.g., "help" should match "I need help" but not "helper"
    const wordBoundaryRegex = new RegExp(`\\b${key}\\b`, 'i');
    if (wordBoundaryRegex.test(lowerMessage)) {
      const responseList = knowledge.responses[key];
      return responseList[Math.floor(Math.random() * responseList.length)];
    }
  }
  
  // Special keyword combinations for bluetooth$
  if (lowerMessage.includes('bluetooth') && lowerMessage.includes('secure')) {
    return "Bluetooth$ uses end-to-end encryption for all Bluetooth transfers, ensuring your financial data remains private and secure.";
  }
  
  if (lowerMessage.includes('verified') && lowerMessage.includes('client')) {
    return "Only verified clients can receive direct Bluetooth payments with Bluetooth$. This verification process ensures maximum security for all transactions.";
  }

  // Handle simple questions
  if (lowerMessage.includes('what is') || lowerMessage.includes('what\'s')) {
    if (lowerMessage.includes('bluetooth$')) {
      return "Bluetooth$ is a secure financial app that allows you to transfer money via Bluetooth, email, or to any verified user account with end-to-end encryption.";
    }
    if (lowerMessage.includes('bluetooth')) {
      return "Bluetooth is a wireless technology standard for exchanging data over short distances. In Bluetooth$, it's used for secure, direct money transfers between nearby devices.";
    }
    if (lowerMessage.includes('stripe')) {
      return "Stripe is a secure payment processing platform that we use to handle all card payments. It provides tokenization, fraud detection, and secure payment processing.";
    }
  }
  
  // Enhanced help responses
  if (lowerMessage.includes('help')) {
    if (lowerMessage.includes('payment')) {
      return "To make a payment with Bluetooth$: 1) Log in to your account, 2) Click 'Send Money', 3) Choose your payment method (Bluetooth, email, or username), 4) Enter the amount and recipient details, 5) Review and confirm your payment.";
    }
    if (lowerMessage.includes('account')) {
      return "For account help: 1) To create an account, click 'Sign Up' and follow the steps, 2) To manage your account, log in and go to 'Profile', 3) For security settings, visit the 'Settings' section after logging in.";
    }
    return "I can help you understand how to use Bluetooth$ for payments, explain features, or troubleshoot issues. What specific aspect would you like to know more about?";
  }
  
  // Stripe specific combination responses
  if (lowerMessage.includes('stripe') && lowerMessage.includes('secure')) {
    return "Bluetooth$ integrates with Stripe's secure payment processing to ensure all card transactions are handled with the highest level of security and PCI compliance.";
  }
  
  if (lowerMessage.includes('card') && lowerMessage.includes('verification')) {
    return "Card verification through our AI-enhanced system and Stripe ensures your payment method is valid and secure before processing any transactions. For testing, you can use cards like 4242 4242 4242 4242.";
  }
  
  // Question-based responses
  if (lowerMessage.includes('?')) {
    if (lowerMessage.includes('fee') || lowerMessage.includes('cost') || lowerMessage.includes('charge')) {
      return "Bluetooth$ charges a small fee of 1.5% for standard transfers. Premium account holders enjoy reduced fees of 0.75%. There are no hidden charges or monthly subscription fees.";
    }
    if (lowerMessage.includes('safe') || lowerMessage.includes('secur')) {
      return "Yes, Bluetooth$ is secure. We use end-to-end encryption for all transfers, verify all receiving clients, and never store payment data permanently on any device.";
    }
    if (lowerMessage.includes('limit')) {
      return "Standard accounts have a transfer limit of $5,000 per day. Premium accounts can transfer up to $25,000 per day. These limits help ensure security and compliance with financial regulations.";
    }
  }
  
  return undefined;
};

/**
 * Finds contextual response based on current path
 * @param currentPath Current page path
 * @param knowledge Knowledge base with contextual responses
 * @returns Contextual response or undefined if no match found
 */
export const findContextualResponse = (currentPath: string, knowledge: KnowledgeBase): string | undefined => {
  // Handle signin/signup pages
  if (currentPath === '/signin' || currentPath === '/login') {
    return "Welcome to Bluetooth$! Sign in to access your account or create a new one to start making secure payments.";
  }
  
  if (currentPath === '/signup' || currentPath === '/register') {
    return "Creating a Bluetooth$ account is quick and secure. Once registered, you can send money via Bluetooth or email to anyone.";
  }
  
  // Handle transaction pages
  if (currentPath === '/transactions' || currentPath.includes('/transactions')) {
    return "Your transaction history shows all payments you've made and received. Each transaction includes details like amount, recipient, date, and method used.";
  }
  
  // Handle payment pages
  if (currentPath === '/payment' || currentPath.includes('/payment')) {
    return "On this page, you can send money using Bluetooth, email, or directly to another user's account. All transfers are secured with end-to-end encryption.";
  }
  
  // Handle profile pages
  if (currentPath === '/profile' || currentPath.includes('/profile')) {
    return "Your profile contains your account information and settings. You can update your details, manage payment methods, and set security preferences here.";
  }
  
  // Handle home page
  if (currentPath === '/' || currentPath === '/home' || currentPath === '/index') {
    return "Welcome to Bluetooth$! Use the navigation to make payments, view your transaction history, or manage your account settings.";
  }
  
  // Direct path match
  if (currentPath && knowledge.contextualResponses[currentPath]) {
    const responses = knowledge.contextualResponses[currentPath];
    return responses[Math.floor(Math.random() * responses.length)];
  }
  
  // Partial path match
  for (const path in knowledge.contextualResponses) {
    if (currentPath.includes(path)) {
      const responses = knowledge.contextualResponses[path];
      return responses[Math.floor(Math.random() * responses.length)];
    }
  }
  
  return undefined;
};

/**
 * Gets a random fallback response
 * @param knowledge Knowledge base with fallback responses
 * @returns Random fallback response
 */
export const getRandomFallback = (knowledge: KnowledgeBase): string => {
  const randomIndex = Math.floor(Math.random() * knowledge.fallbacks.length);
  return knowledge.fallbacks[randomIndex];
};

/**
 * Gets a response that hasn't been used recently
 * @param knowledge Knowledge base with fallback responses
 * @param recentResponses Array of recently used responses to avoid repetition
 * @returns A response that hasn't been used recently
 */
export const getUniqueResponse = (knowledge: KnowledgeBase, recentResponses: string[] = []): string => {
  // Get all available fallback responses
  const availableResponses = [...knowledge.fallbacks];
  
  // Filter out recently used responses to avoid repetition
  const uniqueResponses = availableResponses.filter(response => !recentResponses.includes(response));
  
  // If all responses have been used recently, reset and use any response
  if (uniqueResponses.length === 0) {
    return availableResponses[Math.floor(Math.random() * availableResponses.length)];
  }
  
  // Return a random response from the filtered list
  return uniqueResponses[Math.floor(Math.random() * uniqueResponses.length)];
};

